import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { FLAGS } from '@/config/featureFlags';
import { syncNativeSystemBars } from '@/lib/native/systemBars';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  getEffectiveTheme: () => 'light' | 'dark';
}

const getSystemPreference = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/**
 * Single source of truth for whether the UI should render in dark mode.
 * Explicit "dark" always wins; everything else (system, missing, corrupt)
 * falls back to the OS preference so we never crash-to-light.
 */
export function resolveDark(theme: Theme | null | undefined, prefersDark: boolean): boolean {
  return theme === 'dark' || (theme !== 'light' && prefersDark);
}

/** Apply the resolved theme by adding or removing the `dark` class on <html>. */
export function applyTheme(theme: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  // Vidi's theme is three-state (light/dark/system) and can diverge from the
  // OS setting, so the status bar is driven from here — the same resolved
  // boolean the DOM class above uses — not from matchMedia directly (plan
  // §3.1). Dead code on web: FLAGS.nativeIOS is the literal `false` there.
  if (FLAGS.nativeIOS) {
    void syncNativeSystemBars(theme === 'dark', () => import('@capacitor/core'));
  }
}

export const useTheme = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',

      setTheme: (theme: Theme) => {
        set({ theme });
        const isDark = resolveDark(theme, getSystemPreference() === 'dark');
        applyTheme(isDark ? 'dark' : 'light');
      },

      toggleTheme: () => {
        const currentTheme = get().theme;
        const nextTheme: Theme =
          currentTheme === 'light' ? 'dark' : currentTheme === 'dark' ? 'system' : 'light';
        get().setTheme(nextTheme);
      },

      getEffectiveTheme: () => {
        const theme = get().theme;
        return resolveDark(theme, getSystemPreference() === 'dark') ? 'dark' : 'light';
      },
    }),
    {
      name: 'vidi-theme',
      version: 1,
      onRehydrateStorage: () => (state) => {
        if (state) {
          const isDark = resolveDark(state.theme, getSystemPreference() === 'dark');
          applyTheme(isDark ? 'dark' : 'light');
        }
      },
    },
  ),
);

// Listen for system theme changes
if (typeof window !== 'undefined') {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleChange = () => {
    const theme = useTheme.getState().theme;
    const isDark = resolveDark(theme, getSystemPreference() === 'dark');
    applyTheme(isDark ? 'dark' : 'light');
  };

  mediaQuery.addEventListener('change', handleChange);
}
